import { useCallback, useRef, useState } from 'react';
import { Upload, Film, AlertCircle } from 'lucide-react';

interface UploadSectionProps {
  onFileSelected: (file: File) => void;
}

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_DURATION = 60; // 60 seconds
const ACCEPTED_TYPES = '.mp4,.webm,.mov';

function validateVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Could not read video file'));
    };
    video.src = URL.createObjectURL(file);
  });
}

export function UploadSection({ onFileSelected }: UploadSectionProps) {
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateAndSelect = useCallback(async (file: File) => {
    setError(null);
    setIsValidating(true);

    try {
      if (!file.type.startsWith('video/')) {
        setError('Please select a video file (MP4, WebM, or MOV)');
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setError(`File too large. Maximum size is 100MB (yours: ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
        return;
      }

      const duration = await validateVideoDuration(file);
      if (duration > MAX_DURATION) {
        setError(`Video too long. Maximum duration is 1 minute (yours: ${Math.ceil(duration)}s)`);
        return;
      }

      onFileSelected(file);
    } catch {
      setError('Could not read video file. Please try a different format.');
    } finally {
      setIsValidating(false);
    }
  }, [onFileSelected]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) validateAndSelect(file);
  }, [validateAndSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndSelect(file);
    // Reset input so the same file can be re-selected
    e.target.value = '';
  }, [validateAndSelect]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
      {/* Left column — Heading + Demo */}
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Remove Video Background
          </h1>
          <p className="mt-3 text-xl text-muted-foreground">
            100% Automatically with AI
          </p>
        </div>

        {/* Before-after placeholder */}
        <div className="relative aspect-video rounded-xl overflow-hidden bg-muted border border-border">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-center gap-2">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-lg bg-muted-foreground/10 flex items-center justify-center">
                  <Film className="w-10 h-10 text-muted-foreground/50" />
                </div>
                <span className="text-xs text-muted-foreground">Original</span>
              </div>
              <div className="text-2xl text-muted-foreground/50 font-bold">&rarr;</div>
              <div className="flex flex-col items-center gap-2">
                <div
                  className="w-24 h-24 md:w-32 md:h-32 rounded-lg flex items-center justify-center"
                  style={{
                    backgroundImage: 'repeating-conic-gradient(#80808020 0% 25%, transparent 0% 50%)',
                    backgroundSize: '16px 16px',
                  }}
                >
                  <Film className="w-10 h-10 text-orange-500/60" />
                </div>
                <span className="text-xs text-muted-foreground">Background Removed</span>
              </div>
            </div>
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          No green screen needed. Works with any video — powered by AI.
        </p>
      </div>

      {/* Right column — Upload card */}
      <div className="flex justify-center lg:justify-end">
        <div
          className={`
            w-full max-w-md rounded-2xl border-2 border-dashed p-8 transition-all
            ${isDragging
              ? 'border-orange-500 bg-orange-500/5'
              : 'border-border bg-card hover:border-muted-foreground/30'
            }
          `}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="flex flex-col items-center gap-5 text-center">
            <div className="w-16 h-16 rounded-full bg-orange-500/10 flex items-center justify-center">
              <Upload className="w-7 h-7 text-orange-500" />
            </div>

            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Upload your clip
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Drag & drop or click to browse
              </p>
            </div>

            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isValidating}
              className="w-full px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isValidating ? 'Checking video...' : 'Select Video'}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              onChange={handleFileInput}
              className="hidden"
            />

            <p className="text-xs text-muted-foreground">
              MP4, WebM, MOV &middot; Up to 1 minute &middot; 100MB max
            </p>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm w-full text-left">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
